import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';

type PublicShareWorkspaceAccessContextValue = {
  viewerIsMemberOfPublicWorkspace: boolean;
};

const PublicShareWorkspaceAccessContext = createContext<PublicShareWorkspaceAccessContextValue | undefined>(undefined);

export type PublicShareWorkspaceAccessProviderProps = {
  children: ReactNode;
};

export const PublicShareWorkspaceAccessProvider = memo(({ children }: PublicShareWorkspaceAccessProviderProps) => {
  const { workspaceId } = usePublicWorkspace();
  const { data: workspaces } = useWorkspaces();

  const value = useMemo(
    () => ({
      viewerIsMemberOfPublicWorkspace: !!workspaceId && !!workspaces?.some((workspace) => workspace.id === workspaceId),
    }),
    [workspaceId, workspaces],
  );

  return (
    <PublicShareWorkspaceAccessContext.Provider value={value}>{children}</PublicShareWorkspaceAccessContext.Provider>
  );
});

PublicShareWorkspaceAccessProvider.displayName = 'PublicShareWorkspaceAccessProvider';

export function usePublicShareWorkspaceAccess() {
  const context = useContext(PublicShareWorkspaceAccessContext);

  if (!context) {
    throw 'PublicShareWorkspaceAccessContext used outside of PublicShareWorkspaceAccessProvider';
  }

  return context;
}
